/**
 * PRD.289 — Pharmacy read-only Prescription drawer.
 *
 * Lets a pharmacist review — and download — the prescription for a queue row
 * WITHOUT opening the Patient Workspace (`/patient/:id`). It reuses the shared
 * `usePrescriptionImage` pipeline (the exact `PrescriptionPrintLayout` rendered
 * offscreen at A4, rasterized to a PNG) for both the preview and the download.
 */
import { type CSSProperties } from 'react';
import { Drawer } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { useBrandTokens } from '../../shell/shared';
import { usePrescriptionImage } from './usePrescriptionImage';

export interface PharmacyPrescriptionContext {
  patientId: string;
  encounterId: string;
  patientName: string | null;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  context: PharmacyPrescriptionContext | null;
}

export function PharmacyPrescriptionDrawer({ open, onOpenChange, context }: Props) {
  const brandTokens = useBrandTokens();
  const { imgUrl, rendering, printDisabled, download, captureNode } = usePrescriptionImage(
    open,
    context?.patientId ?? '',
    context?.encounterId ?? '',
    context?.patientName,
  );

  return (
    <>
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        title="Prescription"
        description="Read-only view of this visit's prescription."
        portalStyle={brandTokens as CSSProperties}
        width="min(680px, 94vw)"
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="gradient"
              size="md"
              onClick={download}
              disabled={printDisabled || !imgUrl}
            >
              {rendering ? 'Preparing…' : 'Download Prescription'}
            </Button>
          </div>
        }
      >
        <div style={{ padding: 'var(--sp-5)' }}>
          {printDisabled ? (
            <div
              style={{
                textAlign: 'center',
                padding: 'var(--sp-8)',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
                border: '2px dashed var(--border)',
                borderRadius: 'var(--radius-lg)',
              }}
            >
              No prescription items recorded for this visit.
            </div>
          ) : imgUrl ? (
            <img
              src={imgUrl}
              alt="Prescription"
              style={{
                display: 'block',
                width: '100%',
                height: 'auto',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              }}
            />
          ) : (
            <div
              style={{
                padding: 'var(--sp-8)',
                textAlign: 'center',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
              }}
            >
              Preparing prescription…
            </div>
          )}
        </div>
      </Drawer>

      {/* Offscreen A4 capture node — rendered OUTSIDE the Drawer so the slide
          transform can't interfere with the html2canvas capture. */}
      {captureNode}
    </>
  );
}
